import React from 'react';
import { Button, Grid, Paper } from '@mui/material';
import { Backspace, Check } from '@mui/icons-material';

// Définition des touches du clavier
const keys = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['-', '0', '.'],
    [',', 'Effacer', 'OK'],
];

type NumericPadProps = {
    onKeyPress: (key: string) => void;
    onConfirm?: () => void;
};

/**
 * Composant Clavier Numérique Personnalisé
 * Permet de s'affranchir des claviers natifs et de contrôler strictement la saisie (montants, formats).
 */
export default function NumericPad({ onKeyPress, onConfirm }: NumericPadProps) {
    return (
        <Paper
            elevation={0}
            sx={{
                padding: '4px',
                background: '#f4f4f4',
                borderRadius: '12px 12px 0 0', // Arrondi sur le haut
                maxWidth: '100%',
                margin: '0 auto',
            }}
        >
            <Grid container spacing={1}>
                {keys.flat().map(key => {
                    const isBackspace = key === 'Effacer';
                    const isConfirm = key === 'OK';
                    // Tous les boutons prennent 4 colonnes (1/3 de largeur) pour une grille uniforme
                    const xsValue = 4;

                    // Extraction des styles pour éviter les ternaires imbriqués (SonarQube)
                    let bgColor = '#fff';
                    let textColor = '#333';
                    let hoverBgColor = '#fff'; // Désactivation du hover (identique au bg) pour mobile
                    let activeBgColor = '#fff'; // Désactivation de la couleur au clic (identique au bg)

                    if (isBackspace) {
                        bgColor = '#ffcccb';
                        textColor = '#d32f2f';
                        hoverBgColor = '#ffcccb';
                        activeBgColor = '#ffcccb';
                    } else if (isConfirm) {
                        bgColor = '#c8e6c9';
                        textColor = '#2e7d32';
                        hoverBgColor = '#c8e6c9';
                        activeBgColor = '#c8e6c9';
                    }

                    // Détermination du contenu du bouton (SonarQube)
                    let buttonContent: React.ReactNode = key;
                    if (isBackspace) {
                        buttonContent = <Backspace />;
                    } else if (isConfirm) {
                        buttonContent = <Check />;
                    }

                    return (
                        <Grid item xs={xsValue} key={key}>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={() => {
                                    if (isConfirm && onConfirm) {
                                        onConfirm();
                                    } else {
                                        onKeyPress(key);
                                    }
                                }}
                                sx={{
                                    padding: '8px 0',
                                    fontSize: '1.25rem',
                                    fontWeight: 'bold',
                                    borderRadius: '8px',
                                    backgroundColor: bgColor,
                                    color: textColor,
                                    border: '1px solid #e0e0e0',
                                    boxShadow: '0 2px 0 rgba(0,0,0,0.05)',
                                    '&:hover': {
                                        backgroundColor: `${hoverBgColor} !important`,
                                        color: `${textColor} !important`,
                                    },
                                    '&:active': {
                                        backgroundColor: `${activeBgColor} !important`,
                                        color: `${textColor} !important`,
                                        boxShadow: 'none',
                                        transform: 'translateY(2px)',
                                    },
                                }}
                            >
                                {buttonContent}
                            </Button>
                        </Grid>
                    );
                })}
            </Grid>
        </Paper>
    );
}
